import { useState, useMemo } from 'react'
import { MoreHorizontal, UserPlus, History, LogOut, X } from 'lucide-react'
import type { UserListItem, UserRole } from '@common/types'
import { USER_ROLES, ROLE_LABELS } from '@common/permissions'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { RoleBadge } from '@frontend/components/ui/role-badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@frontend/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@frontend/components/ui/dropdown-menu'
import { Button } from '@frontend/components/ui/button'
import { useFilters, useSort, type FilterDefinition, type SortColumnDef } from '@frontend/lib/filters'
import { FilterBar } from '@frontend/components/filters/FilterBar'
import { SearchFilter } from '@frontend/components/filters/SearchFilter'
import { UpdateRoleModal } from './UpdateRoleModal'
import { RoleHistoryModal } from './RoleHistoryModal'
import { ForceLogoutModal } from './ForceLogoutModal'

interface UsersTableProps {
  users: UserListItem[]
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function UsersTable({ users }: UsersTableProps) {
  const { canManageUser, role: actorRole } = usePermissions()

  // Modal state
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null)
  const [updateRoleOpen, setUpdateRoleOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [forceLogoutOpen, setForceLogoutOpen] = useState(false)

  // Define filters with match functions
  const filterDefinitions = useMemo<FilterDefinition<UserListItem>[]>(
    () => [
      {
        id: 'search',
        label: 'Search',
        type: 'search',
        match: (user, value) => {
          const search = value as string
          if (!search) return true
          return user.email.toLowerCase().includes(search.toLowerCase())
        },
      },
      {
        id: 'role',
        label: 'Role',
        type: 'multiselect',
        match: (user, value) => {
          const roles = value as UserRole[]
          if (roles.length === 0) return true
          return roles.includes(user.role)
        },
      },
    ],
    []
  )

  // Use the filtering hook
  const { filteredData, filterState, setFilter, clearAllFilters, activeFilterCount } = useFilters(
    users,
    filterDefinitions
  )

  const sortColumns = useMemo<SortColumnDef<UserListItem>[]>(
    () => [
      { id: 'email', accessor: (u) => u.email },
      { id: 'created', accessor: (u) => u.createdAt },
    ],
    []
  )

  const { sortedData, toggleSort, isSorted } = useSort(filteredData, sortColumns, {
    field: 'email',
    direction: 'asc',
  })

  // Type-safe accessors for filter values
  const searchValue = (filterState.search as string) || ''
  const roleValue = (filterState.role as UserRole[]) || []

  // Action handlers
  const handleChangeRole = (user: UserListItem) => {
    setSelectedUser(user)
    setUpdateRoleOpen(true)
  }

  const handleViewHistory = (user: UserListItem) => {
    setSelectedUser(user)
    setHistoryOpen(true)
  }

  const handleForceLogout = (user: UserListItem) => {
    setSelectedUser(user)
    setForceLogoutOpen(true)
  }

  // Role filter options
  const roleOptions = USER_ROLES.map((role) => ({
    value: role,
    label: ROLE_LABELS[role],
  }))

  return (
    <>
      {/* Filters */}
      <FilterBar className="mb-4">
        <SearchFilter
          value={searchValue}
          onChange={(value) => setFilter('search', value)}
          placeholder="Search by email..."
        />
        <div className="flex gap-2">
          {roleOptions.map((option) => (
            <Button
              key={option.value}
              variant={roleValue.includes(option.value) ? 'default' : 'outline'}
              size="sm"
              className="h-9"
              onClick={() => {
                const newRoles = roleValue.includes(option.value)
                  ? roleValue.filter((r) => r !== option.value)
                  : [...roleValue, option.value]
                setFilter('role', newRoles)
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-9 px-2 text-muted-foreground">
            <X className="h-4 w-4 mr-1" />
            Clear filters
          </Button>
        )}
      </FilterBar>

      {/* Results count */}
      <div className="text-sm text-muted-foreground mb-4">
        {sortedData.length} of {users.length} user{users.length !== 1 ? 's' : ''}
        {activeFilterCount > 0 && ' (filtered)'}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortableTableHead sorted={isSorted('email')} onSort={() => toggleSort('email')}>
                Email
              </SortableTableHead>
              <TableHead>Role</TableHead>
              <SortableTableHead sorted={isSorted('created')} onSort={() => toggleSort('created')}>
                Created
              </SortableTableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  {activeFilterCount > 0 ? 'No users match your filters' : 'No users found'}
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((user) => {
                const canModify = canManageUser(user.role)

                return (
                  <TableRow key={user.userId}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>
                      <RoleBadge role={user.role} />
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canModify ? (
                            <DropdownMenuItem onClick={() => handleChangeRole(user)}>
                              <UserPlus className="mr-2 h-4 w-4" />
                              Change Role
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem disabled className="text-muted-foreground">
                              <UserPlus className="mr-2 h-4 w-4" />
                              Change Role
                              <span className="ml-auto text-xs">
                                {user.role === 'owner' ? 'Owner' : actorRole === 'admin' ? 'Owner only' : ''}
                              </span>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleViewHistory(user)}>
                            <History className="mr-2 h-4 w-4" />
                            View History
                          </DropdownMenuItem>
                          {canModify && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleForceLogout(user)}
                                className="text-destructive focus:text-destructive"
                              >
                                <LogOut className="mr-2 h-4 w-4" />
                                Force Logout
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modals */}
      <UpdateRoleModal
        key={selectedUser?.userId ?? 'none'}
        user={selectedUser}
        open={updateRoleOpen}
        onOpenChange={setUpdateRoleOpen}
      />
      <RoleHistoryModal user={selectedUser} open={historyOpen} onOpenChange={setHistoryOpen} />
      <ForceLogoutModal user={selectedUser} open={forceLogoutOpen} onOpenChange={setForceLogoutOpen} />
    </>
  )
}
